import React, { useState } from 'react';

import { css } from '@emotion/react';
import { ErrorMessage } from '@hookform/error-message';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useForm, SubmitHandler } from 'react-hook-form';

import LoadingSpinner from '@components/Common/LoadingSpinner';
import TemporaryModal from '@components/Modals/TemporaryModal';

import {
  accessTokenStorage,
  expirationDateStorage,
} from '@storage/authStorage';
import { LoginFormInputs } from '@type/login';

import { useLogin } from '@hooks/api/auth/useAuthAPIs';

import { palette } from '@styles/theme';

import {
  divisionValidateOptions,
  emailValidateOptions,
} from '@constants/validation';

/** 메인화면 로그인 */
const Login = () => {
  /** Input Focus 상태관리 */
  const [divisionFocus, setDivisionFocus] = useState<boolean>(false);
  const [idFocus, setIdFocus] = useState<boolean>(false);
  const [passwordFocus, setPasswordFocus] = useState<boolean>(false);
  /** 비밀번호 평문 표시 여부 (눈 아이콘 토글) */
  const [showPassword, setShowPassword] = useState<boolean>(false);

  /** 임시 로그인 알림 모달 상태관리 */
  const [temporaryModalOpen, setTemporaryModalOpen] = useState<boolean>(false);

  /** react-hook-form Init */
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<LoginFormInputs>();

  const { mutate, isLoading } = useLogin(setTemporaryModalOpen);

  /** validation Error Input name */
  const handleInputNames = () => {
    if (errors.division) {
      return 'division';
    } else if (errors.email) {
      return 'email';
    } else {
      return 'password';
    }
  };

  /** validation Error Tag name  */
  const handleErrorTags = () => {
    if (errors.division) {
      return '[회사코드] ';
    } else if (errors.email) {
      return '[이메일] ';
    } else {
      return '[비밀번호] ';
    }
  };

  /** 로그인 버튼 클릭했을 경우 */
  const onSubmit: SubmitHandler<LoginFormInputs> = async ({
    division,
    email,
    password,
  }) => {
    try {
      const formData = new FormData();
      formData.append('division', division);
      formData.append('email', email);
      formData.append('password', password);

      /** 기존에 남아있던 Localstorage 캐시 삭제 */
      accessTokenStorage.clear();
      expirationDateStorage.clear();

      mutate({
        division,
        email,
        password,
      });
    } catch (error: any) {
      console.log('ERROR');
    }
  };

  return (
    <>
      <div css={container}>
        <div css={wrapper}>
          <div css={logoWrap}>
            <img src="/assets/images/gms_logo_2.png" alt="logo" />
          </div>

          <form css={formWrap} onSubmit={handleSubmit(onSubmit)}>
            <div
              css={[inputWrap, divisionFocus && inputWrapOnFocus]}
              onFocus={() => setDivisionFocus(true)}
              onBlur={() => setDivisionFocus(false)}
            >
              <BusinessOutlinedIcon />
              <input
                css={loginInput}
                type="text"
                autoComplete="off"
                placeholder="회사코드를 입력해 주세요."
                {...register('division', divisionValidateOptions)}
              />
            </div>
            <div
              css={[inputWrap, idFocus && inputWrapOnFocus]}
              onFocus={() => setIdFocus(true)}
              onBlur={() => setIdFocus(false)}
            >
              <MailOutlineIcon />
              <input
                css={loginInput}
                type="email"
                autoComplete="off"
                placeholder="이메일주소를 입력해 주세요."
                {...register('email', emailValidateOptions)}
              />
            </div>
            <div
              css={[inputWrap, passwordFocus && inputWrapOnFocus]}
              onFocus={() => setPasswordFocus(true)}
              onBlur={() => setPasswordFocus(false)}
            >
              <LockOutlinedIcon />
              <input
                css={loginInput}
                type={showPassword ? 'text' : 'password'}
                autoComplete="off"
                placeholder="비밀번호를 입력해 주세요."
                {...register('password', {
                  required: '비밀번호를 입력해 주세요.',
                })}
              />
              <button
                type="button"
                css={toggleButton}
                aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
                // 클릭해도 입력창 커서가 버튼으로 넘어가지 않게. 키보드 Tab 접근은 그대로
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? (
                  <VisibilityOffOutlinedIcon />
                ) : (
                  <VisibilityOutlinedIcon />
                )}
              </button>
            </div>
            {!isValid && (
              <ErrorMessage
                errors={errors}
                name={handleInputNames()}
                render={({ message }) => (
                  <p css={errorMessage}>
                    {handleErrorTags()}
                    {message}
                  </p>
                )}
              />
            )}

            <div css={btnWrap}>
              <button type="submit">로그인</button>
            </div>
          </form>
        </div>
        <div css={markLogo}>
          <img src="/assets/images/mark_logo.png" alt="mark" />
        </div>
      </div>

      {/* 임시계정 로그인 안내 모달 */}
      {temporaryModalOpen && (
        <TemporaryModal
          open={temporaryModalOpen}
          handleOpen={() => setTemporaryModalOpen(false)}
        />
      )}

      {isLoading && <LoadingSpinner />}
    </>
  );
};

export default Login;

const container = css`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const wrapper = css`
  display: flex;
  flex-direction: column;
  width: 480px;
  height: auto;
  box-shadow: 0px 5px 40px 0px rgba(206, 208, 214, 0.5);
  border-radius: 25px;
  background-color: ${palette.white};
  padding: 60px;
`;

const logoWrap = css`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-bottom: 80px;
  img {
    height: 70px;
    object-fit: contain;
  }
`;

const markLogo = css`
  position: absolute;
  bottom: 20px;
  right: 20px;
  img {
    height: 30px;
    filter: grayscale(1);
  }
`;

const formWrap = css`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const loginInput = css`
  width: 100%;
  height: 50px;
  border-radius: 5px;
  padding: 0 20px 0 0;
  background-color: ${palette.white};
  display: flex;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;

  :focus {
    outline: none;
  }

  /* 크롬 자동완성 배경(연파랑)은 UA 스타일이라 background 로 못 덮는다. 흰색 inset 그림자로 가리고 글자색만 맞춘다 */
  :-webkit-autofill,
  :-webkit-autofill:hover,
  :-webkit-autofill:focus {
    box-shadow: 0 0 0 1000px ${palette.white} inset;
    -webkit-text-fill-color: ${palette.black};
  }
`;

const inputWrap = css`
  display: flex;
  align-items: center;
  padding-left: 20px;
  border-radius: 5px;
  border: 1px solid ${palette.gray3};

  /* 입력 항목 아이콘(직계 svg만): 기본은 회색, 포커스 시 테두리와 같은 색.
     토글 버튼 안의 눈 아이콘에는 적용되지 않는다 */
  > svg {
    margin-right: 20px;
    font-size: 20px;
    color: ${palette.gray5};
    transition: color 0.2s;
  }
`;

const inputWrapOnFocus = css`
  border-color: ${palette.primaryColor};
  > svg {
    color: ${palette.primaryColor};
  }
`;

/* 비밀번호 표시/숨김 토글 */
const toggleButton = css`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 16px 0 4px;
  color: ${palette.gray5};
  cursor: pointer;
  svg {
    font-size: 20px;
  }
  &:hover {
    color: ${palette.gray6};
  }
`;

const btnWrap = css`
  // margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  button {
    width: 100%;
    height: 50px;
    border-radius: 5px;
    background-color: ${palette.secondaryColor};
    color: ${palette.white};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    &:hover {
      opacity: 0.8;
      transition: 0.3s;
    }
  }

  .find {
    background-color: ${palette.white};
    color: ${palette.black};
    &:hover {
      background-color: ${palette.gray2};
    }
  }
`;

// const imsi = css`
//   display: flex;
//   position: fixed;
//   bottom: 20px;
//   gap: 20px;
//   .wrap {
//     font-size: 14px;
//     .title {
//       font-weight: 600;
//     }
//   }
// `;

const errorMessage = css`
  color: ${palette.colorDanger};
  font-size: 11px;
  padding: 0 5px;
  line-height: 12px;
`;
